import React, { useEffect, useState } from "react";
import Clipboard from "clipboard";
import { observer } from "mobx-react-lite";
import { Button, ButtonProps, IconButton, SvgIcon, SvgIconProps, Tooltip, Typography, TypographyProps } from "@mui/material";
import { MdContentCopy } from "react-icons/md";
import { useTranslation } from "react-i18next";

// ======================
// ClipboardText
// ======================

interface ClipboardTextProps {
    clipboardId: string;
}

export const ClipboardText: React.FC<ClipboardTextProps & Partial<TypographyProps>> = observer((props) => {
    const { clipboardId, ...rest } = props;
    return (
        <Typography {...rest} id={clipboardId} component={"span"}>
            {props.children}
        </Typography>
    );
});

// ======================
// ClipboardButton
// ======================

interface ClipboardButtonProps {
    clipboardId: string;
    iconButton?: boolean;
    label?: string;
    clipboardText?: string;
    size?: "small" | "medium";
}

export const ClipboardButton: React.FC<ClipboardButtonProps & Partial<ButtonProps>> = observer((props) => {
    const { clipboardId, iconButton, label, clipboardText, size, ...rest } = props;
    const { t } = useTranslation("common");

    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltipResult, setTooltipResult] = useState<"success" | "error" | undefined>();

    const showTooltip = (result: "success" | "error") => {
        setTooltipResult(result);
        setTooltipOpen(true);
        setTimeout(() => setTooltipOpen(false), 1000);
    };

    const getTooltipText = () => {
        if (tooltipResult === "success") {
            return t("clipboard.copied");
        } else if (tooltipResult === "error") {
            return t("clipboard.failed");
        }
        return "";
    };
    useEffect(() => {
        const clipboard = new Clipboard(`#${clipboardId}`);
        clipboard.on("success", () => {
            showTooltip("success");
        });
        clipboard.on("error", (e) => {
            showTooltip("error");
            console.error("Action:", e.action);
            console.error("Trigger:", e.trigger);
            console.error(e.text);
        });
        return () => clipboard.destroy();
    }, [clipboardId]);

    // Prepare clipboard attributes based on whether clipboardText is provided
    const clipboardAttributes = clipboardText ? { "data-clipboard-text": clipboardText } : { "data-clipboard-target": `#${clipboardId}` };

    console.debug("clipboardAttributes", clipboardAttributes);
    if (!iconButton) {
        return (
            <Tooltip title={getTooltipText()} arrow={true} open={tooltipOpen} leaveDelay={0} placement={"top"} id={clipboardId}>
                <Button {...rest} variant={"outlined"} id={clipboardId} {...clipboardAttributes}>
                    {label ?? t("clipboard.copy")}
                </Button>
            </Tooltip>
        );
    } else {
        return (
            <Tooltip title={getTooltipText() || t("clipboard.copy")} arrow={true} leaveDelay={0} placement={"top"} id={clipboardId} open={tooltipOpen}>
                <IconButton size={size} id={clipboardId} {...clipboardAttributes}>
                    <CopyToClipIcon size={size} />
                </IconButton>
            </Tooltip>
        );
    }
});

interface CopyToClipIconProps {
    size?: "small" | "medium";
}

export const CopyToClipIcon: React.FC<CopyToClipIconProps & Partial<SvgIconProps>> = (props) => {
    const { size, ...rest } = props;
    return <MdContentCopy size={size === "small" ? 16 : 24} {...rest} />;
};
